import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { propTypes } from '../../../util/types';
import { PaginationLinks } from '../../../components';
import ManageListingCard from '../../ManageListingsPage/ManageListingCard/ManageListingCard';
import api from '../../../api';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {string} [props.pageName] - Route name for pagination links (defaults to SearchPage)
 * @param {Object} [props.pagePathParams] - Route path params for pagination links
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    // SEO pages (/:slug, /:brandSlug/:modelSlug) render through this panel too.
    // Without these, page 2 links back to the bare SearchPage and drops the
    // brand/category/model the visitor was looking at.
    pageName = 'SearchPage',
    pagePathParams = {},
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const { currentUser, currentUserLoaded } = useSelector(state => state.user);
  const currentUserId = currentUser?.id?.uuid;

  const listingIds = listings
    .filter(l => l.author?.id?.uuid !== currentUserId)
    .map(l => l.id.uuid)
    .join(',');

  useEffect(() => {
    if (typeof window === 'undefined' || !listingIds || !currentUserLoaded) return;
    api.listings
      .batchIncrementListingStatistics({
        listingIds: listingIds.split(','),
        listingStatisticsType: 'searchImpressionsCount',
      })
      .catch(() => {});
  }, [listingIds, currentUserLoaded]);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={pagePathParams}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => (
          <ManageListingCard
            className={css.listingCard}
            key={l.id.uuid}
            listing={l}
            renderSizes={cardRenderSizes(isMapVariant)}
            setActiveListing={setActiveListing}
            disableActions
            hideStats
            hideLabel
            enableBackgroundColorChange
            isOnSearchPage
          />
        ))}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
